import { FinancialLeasePaymentFields } from '../financial-lease-payment-fields.js';

const initializedContainers = new WeakSet();

function initializeContainer(container) {
    if (initializedContainers.has(container)) {
        return;
    }

    initializedContainers.add(container);
    new FinancialLeasePaymentFields(container).setup();

    const output = container.querySelector('[data-lease-term-amount]');

    if (!output) {
        return;
    }

    const calculateUrlTemplate = container.dataset.calculateUrl;
    const commissionOutput = container.querySelector('[data-lease-commission]');
    const grossInvestmentInput = container.querySelector('input[name*="grossInvestment"]');
    const commissionRuleInput = container.querySelector('[name*="commissionRuleId"]');
    const commissionRuleSelect = container.querySelector('select[name*="commissionRuleId"]');
    const outsideDurationNotice = container.querySelector('[data-commission-rule-outside-duration]');
    let controller = null;
    let timer = null;

    const parseValue = (value) =>
        Number(
            String(value)
                .replace(/[^0-9,]/g, '')
                .replace(',', '.'),
        ) || 0;

    const currencyFormatter = new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
    });

    const formatPrice = (price) => (price && price.price !== undefined ? `${currencyFormatter.format(Number(price.price))},-` : '—');

    const clear = () => {
        output.textContent = '—';

        if (commissionOutput) {
            commissionOutput.textContent = '—';
        }
    };

    const filterCommissionRules = () => {
        const duration = Number(container.querySelector('input[type="radio"][name*="duration"]:checked')?.value);

        if (!commissionRuleSelect || !duration) {
            return;
        }

        let lapsed = false;

        for (const option of commissionRuleSelect.options) {
            const bands = option.dataset.durationBands ? JSON.parse(option.dataset.durationBands) : null;
            const fits =
                !bands ||
                bands.some(([minimum, maximum]) => (minimum === null || duration >= minimum) && (maximum === null || duration <= maximum));

            option.hidden = !fits;
            option.disabled = !fits;
            lapsed ||= !fits && option.selected;
        }

        if (lapsed) {
            commissionRuleSelect.value = '';
        }

        if (outsideDurationNotice) {
            outsideDurationNotice.hidden = !lapsed;
        }
    };

    const selectedCommissionRuleId = () => {
        const checked = container.querySelector('[name*="commissionRuleId"]:checked');

        return checked ? checked.value : commissionRuleInput?.value || '';
    };

    const calculate = () => {
        const grossInvestment = grossInvestmentInput ? grossInvestmentInput.value.trim() : '';

        if (parseValue(grossInvestment) < 1) {
            clear();
            return;
        }

        const duration = container.querySelector('input[type="radio"][name*="duration"]:checked')?.value;
        if (!duration) {
            clear();
            return;
        }

        const commissionRuleId = selectedCommissionRuleId();
        if (!commissionRuleId) {
            clear();
            return;
        }

        const downPayment = container.querySelector('.financial-lease-input[name*="downPayment"]');
        const balloonPayment = container.querySelector('.financial-lease-input[name*="balloonPayment"]');

        controller?.abort();
        controller = new AbortController();

        const formData = new FormData();
        formData.append('lease_proposition[grossInvestment]', grossInvestment);
        formData.append('lease_proposition[duration]', duration);
        formData.append('lease_proposition[downPayment]', downPayment?.value || '0');
        formData.append('lease_proposition[balloonPayment]', balloonPayment?.value || '0');

        const calculateUrl = calculateUrlTemplate.replace('COMMISSION_RULE_ID', commissionRuleId);

        fetch(calculateUrl, { method: 'POST', body: formData, signal: controller.signal })
            .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Calculation failed'))))
            .then((data) => {
                output.textContent = formatPrice(data.monthlyPayment);

                if (commissionOutput) {
                    commissionOutput.textContent = formatPrice(data.commission);
                }
            })
            .catch((error) => {
                if (error.name === 'AbortError') {
                    return;
                }
                clear();
            });
    };
    const debouncedCalculate = () => {
        clearTimeout(timer);
        timer = setTimeout(calculate, 300);
    };

    container.querySelectorAll('.financial-lease-input, .financial-lease-range').forEach((input) => {
        input.addEventListener('input', debouncedCalculate);
    });
    grossInvestmentInput?.addEventListener('input', debouncedCalculate);
    container.querySelectorAll('input[type="radio"][name*="duration"]').forEach((input) => {
        input.addEventListener('change', filterCommissionRules);
    });
    commissionRuleSelect?.addEventListener('change', () => {
        if (outsideDurationNotice) {
            outsideDurationNotice.hidden = true;
        }
    });
    container.querySelectorAll('input[type="radio"], select[name*="commissionRuleId"]').forEach((input) => {
        input.addEventListener('change', calculate);
    });

    filterCommissionRules();
    calculate();
}

function initialize(root) {
    for (const container of root.querySelectorAll('[data-lease-step]')) {
        initializeContainer(container);
    }
}

window.addEventListener('AutoWise.Admin.ModalContentUpdated', (event) => {
    const modal = event.detail.modal;

    if (!(modal instanceof HTMLDialogElement)) {
        throw new Error('Modal is not an instance of HTMLDialogElement');
    }

    initialize(modal);
});

initialize(document);
